import { traceCrash, traceCrashThrottled } from '../debug/crashTrace';
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const IDLE_WARNING_TIMEOUT_MS = 60 * 1000;

export type LogoutReason = 'inactivity';
export type SessionEventType =
  | 'activity'
  | 'idlePromptOpened'
  | 'idlePromptClosed'
  | 'logout'
  | 'refreshCompleted'
  | 'criticalOperationChanged';

export interface IIdlePromptState {
  startedAt: number;
  deadlineAt: number;
}

export interface ISessionEvent {
  type: SessionEventType;
  ownerTabId: string;
  timestamp: number;
  payload?: Record<string, unknown>;
}

export interface IRefreshResultPayload {
  timestamp: number;
  ok: boolean;
  data: unknown | null;
}

export interface ICriticalOperationState {
  ownerTabId: string;
  pending: number;
  expiresAt: number;
}

const SESSION_CHANNEL_NAME = 'epos-session-sync';
const LAST_ACTIVITY_KEY = 'epos:last-activity-at';
const IDLE_PROMPT_KEY = 'epos:idle-prompt';
const LOGOUT_REASON_KEY = 'epos:logout-reason';
const CRITICAL_OPERATIONS_KEY = 'epos:critical-operations';
const CRITICAL_OPERATION_TTL_MS = 2 * 60 * 1000;
export const REFRESH_LOCK_KEY = 'epos:refresh-lock';
export const REFRESH_RESULT_KEY = 'epos:refresh-result';

const createTabId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const SESSION_TAB_ID = createTabId();

let sessionBroadcastChannel: BroadcastChannel | null | undefined;

const createSessionChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  return new BroadcastChannel(SESSION_CHANNEL_NAME);
};

const getBroadcastChannel = (): BroadcastChannel | null => {
  if (sessionBroadcastChannel === undefined) {
    sessionBroadcastChannel = createSessionChannel();
  }

  return sessionBroadcastChannel;
};

const parseJson = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const getLastActivityAt = (): number => {
  const value = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY));

  if (!Number.isFinite(value) || value <= 0) {
    const now = Date.now();
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    return now;
  }

  return value;
};

export const markUserActivity = (timestamp = Date.now()) => {
  window.localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  traceCrashThrottled('session.markUserActivity', 'session.markUserActivity', { timestamp }, 5000);
  broadcastSessionEvent('activity', { timestamp });
};

export const getIdlePromptState = (): IIdlePromptState | null => {
  return parseJson<IIdlePromptState>(window.localStorage.getItem(IDLE_PROMPT_KEY));
};

export const openIdlePrompt = (startedAt: number): IIdlePromptState => {
  const existing = getIdlePromptState();
  if (existing) {
    return existing;
  }

  const idlePrompt: IIdlePromptState = {
    startedAt,
    deadlineAt: startedAt + IDLE_WARNING_TIMEOUT_MS,
  };

  window.localStorage.setItem(IDLE_PROMPT_KEY, JSON.stringify(idlePrompt));
  broadcastSessionEvent('idlePromptOpened', idlePrompt as unknown as Record<string, unknown>);

  return idlePrompt;
};

export const closeIdlePrompt = (options?: { broadcast?: boolean }) => {
  const existing = getIdlePromptState();

  if (!existing) {
    return false;
  }

  window.localStorage.removeItem(IDLE_PROMPT_KEY);

  if (options?.broadcast !== false) {
    broadcastSessionEvent('idlePromptClosed');
  }

  return true;
};

export const setLogoutNotice = (reason: LogoutReason) => {
  window.localStorage.setItem(LOGOUT_REASON_KEY, reason);
};

export const getLogoutNotice = (): LogoutReason | null => {
  const reason = window.localStorage.getItem(LOGOUT_REASON_KEY);
  return reason === 'inactivity' ? reason : null;
};

export const clearLogoutNotice = () => {
  window.localStorage.removeItem(LOGOUT_REASON_KEY);
};

export const resetSessionActivity = () => {
  window.localStorage.removeItem(IDLE_PROMPT_KEY);
  window.localStorage.removeItem(LOGOUT_REASON_KEY);
  window.localStorage.removeItem(REFRESH_LOCK_KEY);
  window.localStorage.removeItem(REFRESH_RESULT_KEY);
  window.localStorage.removeItem(CRITICAL_OPERATIONS_KEY);
  markUserActivity();
};

export const emitLogout = (reason: LogoutReason) => {
  setLogoutNotice(reason);
  broadcastSessionEvent('logout', { reason });
};

export const getRefreshResult = (): IRefreshResultPayload | null => {
  return parseJson<IRefreshResultPayload>(window.localStorage.getItem(REFRESH_RESULT_KEY));
};

export const setRefreshResult = (payload: IRefreshResultPayload) => {
  window.localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify(payload));
  broadcastSessionEvent('refreshCompleted', payload as unknown as Record<string, unknown>);
};

const getCriticalOperationState = (): ICriticalOperationState | null => {
  const state = parseJson<ICriticalOperationState>(
    window.localStorage.getItem(CRITICAL_OPERATIONS_KEY)
  );

  if (!state || state.expiresAt <= Date.now() || state.pending <= 0) {
    window.localStorage.removeItem(CRITICAL_OPERATIONS_KEY);
    return null;
  }

  return state;
};

export const getPendingCriticalOperations = (): number => {
  return getCriticalOperationState()?.pending ?? 0;
};

const setPendingCriticalOperations = (value: number) => {
  const safeValue = Math.max(0, value);

  if (safeValue === 0) {
    window.localStorage.removeItem(CRITICAL_OPERATIONS_KEY);
  } else {
    const state: ICriticalOperationState = {
      ownerTabId: SESSION_TAB_ID,
      pending: safeValue,
      expiresAt: Date.now() + CRITICAL_OPERATION_TTL_MS,
    };
    window.localStorage.setItem(CRITICAL_OPERATIONS_KEY, JSON.stringify(state));
  }

  broadcastSessionEvent('criticalOperationChanged', { pending: safeValue });
};

export const beginCriticalOperation = () => {
  const pending = getPendingCriticalOperations() + 1;
  traceCrash('session.critical.begin', {
    before: pending - 1,
    after: pending,
    tabId: SESSION_TAB_ID,
  });
  setPendingCriticalOperations(pending);
  markUserActivity();
};

export const endCriticalOperation = () => {
  const state = getCriticalOperationState();

  traceCrash('session.critical.end.request', {
    state,
    tabId: SESSION_TAB_ID,
  });

  if (state && state.ownerTabId !== SESSION_TAB_ID) {
    traceCrash('session.critical.end.ignored-different-owner', {
      state,
      tabId: SESSION_TAB_ID,
    });
    return;
  }

  const pending = Math.max(0, (state?.pending ?? 1) - 1);
  traceCrash('session.critical.end.apply', {
    before: state?.pending ?? 1,
    after: pending,
    tabId: SESSION_TAB_ID,
  });
  setPendingCriticalOperations(pending);
  markUserActivity();
};

export const broadcastSessionEvent = (
  type: SessionEventType,
  payload?: Record<string, unknown>
): ISessionEvent => {
  const event: ISessionEvent = {
    type,
    ownerTabId: SESSION_TAB_ID,
    timestamp: Date.now(),
    payload,
  };

  if (type === 'activity') {
    traceCrashThrottled(
      'session.broadcast.activity',
      'session.broadcast.activity',
      { ownerTabId: SESSION_TAB_ID },
      5000
    );
  } else {
    traceCrash('session.broadcast', {
      type,
      ownerTabId: SESSION_TAB_ID,
      payload,
    });
  }

  getBroadcastChannel()?.postMessage(event);

  return event;
};

export const subscribeToSessionEvents = (handler: (event: ISessionEvent) => void) => {
  const channel = createSessionChannel();

  const onChannelMessage = (message: MessageEvent<ISessionEvent>) => {
    if (message.data) {
      handler(message.data);
    }
  };

  const onStorage = (event: StorageEvent) => {
    if (!event.key) {
      return;
    }

    if (event.key === LAST_ACTIVITY_KEY) {
      handler({
        type: 'activity',
        ownerTabId: 'storage',
        timestamp: Date.now(),
      });
      return;
    }

    if (event.key === IDLE_PROMPT_KEY) {
      handler({
        type: event.newValue ? 'idlePromptOpened' : 'idlePromptClosed',
        ownerTabId: 'storage',
        timestamp: Date.now(),
      });
      return;
    }

    if (event.key === LOGOUT_REASON_KEY && event.newValue) {
      handler({
        type: 'logout',
        ownerTabId: 'storage',
        timestamp: Date.now(),
        payload: { reason: event.newValue },
      });
      return;
    }

    if (event.key === REFRESH_RESULT_KEY && event.newValue) {
      handler({
        type: 'refreshCompleted',
        ownerTabId: 'storage',
        timestamp: Date.now(),
      });
      return;
    }

    if (event.key === CRITICAL_OPERATIONS_KEY) {
      handler({
        type: 'criticalOperationChanged',
        ownerTabId: 'storage',
        timestamp: Date.now(),
      });
    }
  };

  channel?.addEventListener('message', onChannelMessage);
  window.addEventListener('storage', onStorage);

  return () => {
    channel?.removeEventListener('message', onChannelMessage);
    channel?.close();
    window.removeEventListener('storage', onStorage);
  };
};
